import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Grid, TextField, MenuItem, Paper,
    Checkbox, FormControlLabel, Dialog, DialogContent,
    DialogTitle, Stack, IconButton, CircularProgress
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import {
    PieChart, Pie, Cell
} from 'recharts';
import { LineChart, Line } from 'recharts';
import { getDailyResponseTrends } from '../controllers/formResponse.controller';
import { getResponseStatistics } from '../controllers/formResponse.controller';
import { getAverageScoreByForm } from '../controllers/formResponse.controller';
import { getTopFormsAndUsers } from '../controllers/formResponse.controller';
import { downloadExcelReport } from '../controllers/formResponse.controller';
import { getFormResponseCounts } from '../controllers/formResponse.controller';
import { ToastContainer, toast } from 'react-toastify';

import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';





const StatCard = ({ icon, label, value, color }) => (
    <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, fontSize: 40 }}>{icon}</Box>
        <Box>
            <Typography variant="subtitle1">{label}</Typography>
            <Typography variant="h5" fontWeight="bold">{value}</Typography>
        </Box>
    </Paper>
);

const DashboardAnalytics = () => {
    const [stats, setStats] = useState(null);
    const [formChartData, setFormChartData] = useState([]);
    const COLORS = ['#2e7d32', '#d32f2f', '#fbc02d'];
    const [dailyTrend, setDailyTrend] = useState([]);
    const [avgScores, setAvgScores] = useState([]);
    const [topForms, setTopForms] = useState([]);
    const [topUsers, setTopUsers] = useState([]);
    const [pieData, setPieData] = useState([]);



    const toEnglishDigits = (str = '') => str.replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));

    const toISOStartOfDay = (dateObj) => {
        if (!dateObj) return '';
        const d = dateObj.toDate();
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
    };

    const toISOEndOfDay = (dateObj) => {
        if (!dateObj) return '';
        const d = dateObj.toDate();
        d.setHours(23, 59, 59, 999);
        return d.toISOString();
    };


    const today = new DateObject({ date: new Date(), calendar: persian, locale: persian_fa });
    const fourteenDaysAgo = new DateObject({ date: new Date(), calendar: persian, locale: persian_fa }).subtract(14, 'days');

    const [filters, setFilters] = useState({
        search: '',
        type: '',
        startDate: fourteenDaysAgo, // DateObject
        endDate: today,             // DateObject
    });


    const fetchStats = async () => {
        try {
            const res = await getResponseStatistics(buildApiFilters()); // از کنترلر کلاینت
            setStats(res);
        } catch (err) {
            console.error('خطا در fetchStats:', err);
            toast.error('❌ خطا در دریافت آمار کلی پاسخ‌ها');
            setStats(null);
        }
    };


    const fetchChartData = async () => {
        try {
            const data = await getFormResponseCounts(buildApiFilters());
            setFormChartData(data);
        } catch (err) {
            toast.error('خطا در دریافت داده نمودار فرم‌ها:', err);
        }
    };

    const fetchDailyTrend = async () => {
        try {
            const data = await getDailyResponseTrends(buildApiFilters());
            setDailyTrend(data);
        } catch (err) {
            toast.error('خطا در دریافت روند پاسخ‌ها:', err);
        }
    };

    const fetchAverageScores = async () => {
        try {
            const data = await getAverageScoreByForm(buildApiFilters());
            setAvgScores(data);
        } catch (err) {
            toast.error('خطا در دریافت میانگین امتیازها:', err);
        }
    };

    const fetchTops = async () => {
        try {
            const res = await getTopFormsAndUsers(buildApiFilters());
            setTopForms(res.topForms || []);
            setTopUsers(res.topUsers || []);
        } catch (err) {
            toast.error('خطا در دریافت لیست برترین‌ها:', err);
        }
    };


    const buildApiFilters = () => ({
        ...filters,
        startDate: toISOStartOfDay(filters.startDate),
        endDate: toISOEndOfDay(filters.endDate),
    });


    const fetchAllWithFilters = () => {
        fetchStats();
        fetchChartData();
        fetchDailyTrend();
        fetchAverageScores();
        fetchTops();
    };


    useEffect(() => {
        fetchAllWithFilters();
    }, []);








    if (!stats) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3 }}>
            <ToastContainer position="top-right" />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                <TextField
                    label="جستجو در فرم"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    size="small"
                />
                <TextField
                    select
                    label="نوع پروژه"
                    value={filters.type}
                    onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                    size="small"
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">همه</MenuItem>
                    <MenuItem value="trial">آزمایشی</MenuItem>
                    <MenuItem value="main">اصلی</MenuItem>
                    <MenuItem value="enterprise">سازمانی</MenuItem>
                </TextField>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }}>
                    {/* از تاریخ */}
                    <Box sx={{ minWidth: 170 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5, fontSize: 14, fontWeight: 500 }}
                        >
                            از تاریخ
                        </Typography>

                        <DatePicker
                            calendar={persian}
                            locale={persian_fa}
                            format="YYYY/MM/DD"
                            value={filters.startDate || null}
                            onChange={(date) => setFilters((prev) => ({ ...prev, startDate: date || null }))}
                            style={{ width: 170, height: 40 }}
                        />
                    </Box>

                    {/* تا تاریخ */}
                    <Box sx={{ minWidth: 170 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5, fontSize: 14, fontWeight: 500 }}
                        >
                            تا تاریخ
                        </Typography>

                        <DatePicker
                            calendar={persian}
                            locale={persian_fa}
                            format="YYYY/MM/DD"
                            value={filters.endDate || null}
                            onChange={(date) => setFilters((prev) => ({ ...prev, endDate: date || null }))}
                            style={{ width: 170, height: 40 }}
                        />
                    </Box>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => fetchAllWithFilters()}
                >
                    اعمال فیلتر
                </Button>
            </Box>

            <Typography variant="h5" mb={3}>📊 آمار کلی پاسخ‌ها</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}><StatCard icon={<AssessmentIcon />} label="کل پاسخ‌ها" value={stats.totalResponses} color="#1976d2" /></Grid>
                <Grid item xs={12} md={4}><StatCard icon={<TodayIcon />} label="پاسخ‌های امروز" value={stats.todayResponses} color="#43a047" /></Grid>
                <Grid item xs={12} md={4}><StatCard icon={<CalendarMonthIcon />} label="پاسخ‌های این ماه" value={stats.monthResponses} color="#ff9800" /></Grid>
                <Grid item xs={12} md={4}><StatCard icon={<CheckCircleIcon />} label="تأیید شده" value={stats.acceptedResponses} color="#2e7d32" /></Grid>
                <Grid item xs={12} md={4}><StatCard icon={<CancelIcon />} label="رد شده" value={stats.rejectedResponses} color="#d32f2f" /></Grid>
                <Grid item xs={12} md={4}><StatCard icon={<HourglassEmptyIcon />} label="در انتظار بررسی" value={stats.pendingResponses} color="#fbc02d" /></Grid>
            </Grid>
            <Box sx={{ mt: 6 }}>
                <Typography variant="h6" mb={2}>📈 تعداد پاسخ‌ها به تفکیک فرم</Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={formChartData} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="title" type="category" />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#1976d2" name="تعداد پاسخ‌ها" />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 6 }}>
                <Typography variant="h6" mb={2}>📊 توزیع وضعیت ارزیابی پاسخ‌ها</Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label
                        >
                            {pieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" />
                    </PieChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 6 }}>
                <Typography variant="h6" mb={2}>📉 روند پاسخ‌دهی در ۳۰ روز اخیر</Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v) => {
                                // اگر ISO بود
                                const d = new Date(v);
                                if (!isNaN(d.getTime())) return new Intl.DateTimeFormat('fa-IR').format(d);
                                return v; // اگر format غیرقابل parse بود همون رو بده
                            }}
                        />

                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" name="تعداد پاسخ‌ها" stroke="#1976d2" />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 6 }}>
                <Typography variant="h6" mb={2}>📊 میانگین امتیاز به تفکیک فرم</Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={avgScores} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis type="category" dataKey="title" />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="avgScore" fill="#1976d2" name="میانگین امتیاز" />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            <Grid container spacing={3} sx={{ mt: 4 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" mb={2}>📋 فرم‌های پُرپاسخ</Typography>
                    {topForms.map((form, i) => (
                        <Typography key={i}>• {form.title} — {form.count} پاسخ</Typography>
                    ))}
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" mb={2}>👥 کاربران فعال‌تر</Typography>
                    {topUsers.map((user, i) => (
                        <Typography key={i}>• {user.fullName || user.email} — {user.count} پاسخ</Typography>
                    ))}
                </Grid>
            </Grid>
            <Button
                variant="contained"
                color="success"
                onClick={downloadExcelReport}
                sx={{ mt: 2 }}
            >
                📥 دانلود گزارش اکسل
            </Button>
        </Box>
    );
};

export default DashboardAnalytics;
